import { useCallback, useMemo } from 'react';

import { Tooltip, styled } from '@material-ui/core';

import { RiDeleteBinLine, RiRefreshLine } from '@remixicon/react';
import { Editor } from '@tiptap/core';

import { ButtonIcon, Flex, Select } from '@backstage/ui';

import { AvailableMetric } from '@spotify/confidence-editor-react';
import { BlockReorderButtons } from '@spotify/confidence-editor-react';
import { BlockCommentButton } from '@spotify/confidence-editor-react';
import { ToolbarCard } from '@spotify/confidence-editor-react';
import { closeAllDropdowns } from '@spotify/confidence-editor-react';
import { CommentZoneProvider } from '@spotify/plugin-confidence-comments-react';

const StyledSelect = styled(Select)({
  minWidth: '200px',
  '& .bui-SelectTrigger': {
    fontSize: 'var(--bui-font-size-2)',
    border: 'none',
  },
});

type MetricCardToolbarProps = {
  editor: Editor;
  metricId: string | null;
  metrics: AvailableMetric[] | undefined;
  zoneId?: string;
  onSelect: (metricId: string) => void;
  onDelete: () => void;
  onToolbarActiveChange: (active: boolean) => void;
  getPos: () => number | undefined;
  isStale?: boolean;
};

export const MetricCardToolbar = ({
  editor,
  metricId,
  metrics,
  zoneId,
  onSelect,
  onDelete,
  onToolbarActiveChange,
  getPos,
  isStale,
}: MetricCardToolbarProps) => {
  const handleOpenChange = useCallback(
    (open: boolean) => {
      onToolbarActiveChange(open);
      if (open) {
        closeAllDropdowns();
        const pos = getPos();
        if (typeof pos === 'number') {
          editor.commands.setNodeSelection(pos);
        }
      }
    },
    [editor, getPos, onToolbarActiveChange],
  );

  const selectOptions = useMemo(
    () =>
      (metrics ?? []).map(metric => ({
        label: metric.name,
        value: metric.id,
        description:
          metric.type === 'guardrail' ? 'Guardrail metric' : 'Success metric',
      })),
    [metrics],
  );

  const handleRefresh = useCallback(() => {
    if (metricId) {
      onSelect(metricId);
    }
  }, [metricId, onSelect]);

  return (
    <ToolbarCard p="0.5">
      <Flex gap="0.5">
        <BlockReorderButtons getPos={getPos} editor={editor} />
        <StyledSelect
          aria-label="Select metric"
          name="metric-card-hover-select"
          value={metricId || ''}
          onChange={(newValue: unknown) => {
            if (!newValue) return;
            const selectedValue = Array.isArray(newValue)
              ? newValue[0]
              : newValue;
            if (!selectedValue) return;
            onSelect(String(selectedValue));
          }}
          onOpenChange={handleOpenChange}
          options={selectOptions}
          placeholder="Select a metric..."
          isDisabled={(metrics ?? []).length === 0}
        />
        {isStale ? (
          <Tooltip title="Update with latest values">
            <ButtonIcon
              aria-label="Update with latest values"
              size="small"
              icon={<RiRefreshLine color="var(--bui-fg-warning)" />}
              variant="tertiary"
              onClick={handleRefresh}
            />
          </Tooltip>
        ) : undefined}
        {zoneId && (
          <CommentZoneProvider zone={zoneId}>
            <BlockCommentButton />
          </CommentZoneProvider>
        )}
        <Tooltip title="Delete metric card">
          <ButtonIcon
            aria-label="Delete metric card"
            onClick={onDelete}
            size="small"
            icon={<RiDeleteBinLine />}
            variant="tertiary"
          />
        </Tooltip>
      </Flex>
    </ToolbarCard>
  );
};
